import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { DayPicker, DateRange } from 'react-day-picker';
import { format } from 'date-fns';
import 'react-day-picker/style.css';

interface DateRangePickerProps {
  startDate: string;
  endDate: string;
  onApply: (startDate: string, endDate: string) => void;
  onCancel: () => void;
}

export function DateRangePicker({ startDate, endDate, onApply, onCancel }: DateRangePickerProps) {
  const [range, setRange] = useState<DateRange | undefined>(() => {
    const from = startDate ? new Date(startDate) : undefined;
    const to = endDate ? new Date(endDate) : undefined;
    return from || to ? { from, to } : undefined;
  });

  const containerRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setPosition({
        top: rect.bottom + 8,
        left: rect.left,
      });
    }
  }, []);

  const handleApply = () => {
    if (range?.from && range?.to) {
      onApply(
        format(range.from, 'yyyy-MM-dd'),
        format(range.to, 'yyyy-MM-dd')
      );
    }
  };

  return (
    <div ref={containerRef} style={{ display: 'inline-block' }}>
      {createPortal(
        <div
          style={{
            position: 'fixed',
            top: position.top,
            left: position.left,
            zIndex: 99999,
            backgroundColor: 'white',
            borderRadius: '12px',
            boxShadow: '0 4px 24px rgba(0,0,0,0.15)',
            padding: '16px',
          }}
        >
          <style>{`
            .rdp-root {
              --rdp-accent-color: #5c6ac4;
              --rdp-accent-background-color: #5c6ac420;
              --rdp-selected-border: 2px solid #5c6ac4;
              --rdp-range_middle-background-color: #e8eaf6;
              --rdp-range_middle-color: #1a1a2e;
              font-family: inherit;
            }
            .rdp-day_button {
              border-radius: 8px;
            }
            .rdp-selected .rdp-day_button {
              background-color: #5c6ac4;
              color: white;
            }
            .rdp-range_middle .rdp-day_button {
              background-color: #e8eaf6;
              color: #1a1a2e;
            }
            .rdp-today .rdp-day_button {
              font-weight: bold;
              border: 1px solid #5c6ac4;
            }
            .rdp-chevron {
              fill: #5c6ac4;
            }
          `}</style>

          <DayPicker
            mode="range"
            selected={range}
            onSelect={setRange}
            numberOfMonths={2}
            showOutsideDays
            disabled={{ after: new Date() }}
          />

          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginTop: '16px',
            paddingTop: '16px',
            borderTop: '1px solid #e4e5e7',
          }}>
            <div style={{ fontSize: '14px', color: '#637381' }}>
              {range?.from && range?.to ? (
                <>
                  <strong>{format(range.from, 'MMM d, yyyy')}</strong>
                  {' - '}
                  <strong>{format(range.to, 'MMM d, yyyy')}</strong>
                </>
              ) : range?.from ? (
                <>Select end date</>
              ) : (
                <>Select date range</>
              )}
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={onCancel}
                style={{
                  padding: '8px 16px',
                  borderRadius: '6px',
                  border: '1px solid #c4cdd5',
                  backgroundColor: 'white',
                  cursor: 'pointer',
                  fontSize: '14px',
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleApply}
                disabled={!range?.from || !range?.to}
                style={{
                  padding: '8px 16px',
                  borderRadius: '6px',
                  border: 'none',
                  backgroundColor: range?.from && range?.to ? '#5c6ac4' : '#c4cdd5',
                  color: 'white',
                  cursor: range?.from && range?.to ? 'pointer' : 'not-allowed',
                  fontSize: '14px',
                  fontWeight: '500',
                }}
              >
                Apply
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
